import "./StripeNotice.scss";
import { ExportOutlined } from "@ant-design/icons";
import { Result, Skeleton } from "antd";
import { VyneModal } from "@vyne-shared/components/vyneModal/VyneModal";
import { antdModalV5, create, useModal } from "@ebay/nice-modal-react";
import { useStripeSettingsFormLink } from "api/clients/useStripeSettingsFormLink";

interface StripeNoticeModalProps {
    readonly stripeErrorReasons: string[];
}

/** Modal to display a list of fields that need to be updated in Stripe's settings, and a link to update. */
export const StripeNoticeModal = create<StripeNoticeModalProps>(({ stripeErrorReasons }) => {
    const modal = useModal();
    const { isPending, isError, data } = useStripeSettingsFormLink();

    return (
        <VyneModal
            {...antdModalV5(modal)}
            className="vyne-stripe-notice-modal"
            okText="Update Stripe Info"
            okButtonProps={{
                icon: <ExportOutlined />,
                onClick: () => window.open(data?.url, "_blank"),
                disabled: isPending || isError,
            }}
            title="Update Stripe Business Information"
        >
            {isError && (
                <Result
                    status="500"
                    title="Unable to load Stripe settings"
                    subTitle="There was a problem loading Stripe settings. Contact support if this error persists."
                />
            )}
            {isPending && <Skeleton active />}
            {!isError && !isPending && <StripeNoticeContent stripeErrorReasons={stripeErrorReasons} />}
        </VyneModal>
    );
});

const StripeNoticeContent = ({ stripeErrorReasons }: { readonly stripeErrorReasons: string[] }) => (
    <>
        {stripeErrorReasons.length > 0 ? (
            <>
                <p>To keep using Vyne Payments, please fix the following issues:</p>
                <ul>
                    {stripeErrorReasons.map((field) => (
                        <li key={field}>{field}</li>
                    ))}
                </ul>
            </>
        ) : (
            <p>Update your Stripe business information to continue using Vyne Payments.</p>
        )}
        <p>Once all fields are updated, refresh the page to continue transacting.</p>
    </>
);
